import { useRef } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, jsonOrThrow, retryOnce } from "./api";
import type {
  Company,
  CompanyRow,
  Contact,
  ContactSummary,
  HistoryPage,
  WholeList,
} from "../../shared/types/directory";
import type { CompanyAddress } from "../../shared/types/link";

/**
 * The organisation's directory, through the app's own API (src/domains/directory), never from Link directly: the
 * Worker asks Link for each answer, live, and nothing of a record is kept beyond what the page shows. A record is
 * asked for again whenever it is shown.
 */

const COMPANIES_KEY = ["companies"] as const;
const CONTACTS_KEY = ["contacts"] as const;
const companyKey = (id: string) => ["company", id] as const;
const contactKey = (id: string) => ["contact", id] as const;
const companyContactsKey = (id: string) => ["company", id, "contacts"] as const;
const historyKey = (kind: "company" | "contact", id: string) => [kind, id, "history"] as const;

/** Every customer, for the page to sort and search (./listing.ts). */
export function useCompanies() {
  return useQuery({
    queryKey: COMPANIES_KEY,
    queryFn: async () => jsonOrThrow<WholeList<CompanyRow>>(await api.api.v1.companies.$get()),
    staleTime: 15_000,
    retry: retryOnce,
  });
}

/** Every contact, for the page to sort and search. */
export function useContacts() {
  return useQuery({
    queryKey: CONTACTS_KEY,
    queryFn: async () => jsonOrThrow<WholeList<ContactSummary>>(await api.api.v1.contacts.$get()),
    staleTime: 15_000,
    retry: retryOnce,
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: companyKey(id),
    queryFn: async () =>
      (await jsonOrThrow<{ company: Company }>(await api.api.v1.companies[":id"].$get({ param: { id } }))).company,
    staleTime: 0,
    retry: retryOnce,
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: contactKey(id),
    queryFn: async () =>
      (await jsonOrThrow<{ contact: Contact }>(await api.api.v1.contacts[":id"].$get({ param: { id } }))).contact,
    staleTime: 0,
    retry: retryOnce,
  });
}

/** A customer's contacts. */
export function useCompanyContacts(id: string) {
  return useQuery({
    queryKey: companyContactsKey(id),
    queryFn: async () =>
      jsonOrThrow<WholeList<ContactSummary>>(await api.api.v1.companies[":id"].contacts.$get({ param: { id } })),
    staleTime: 0,
    retry: retryOnce,
  });
}

/** What happened to a record, newest first, a page at a time as Link pages it. */
export function useHistory(kind: "company" | "contact", id: string) {
  return useInfiniteQuery({
    queryKey: historyKey(kind, id),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const query = pageParam === undefined ? {} : { cursor: pageParam };
      const res =
        kind === "company"
          ? await api.api.v1.companies[":id"].history.$get({ param: { id }, query })
          : await api.api.v1.contacts[":id"].history.$get({ param: { id }, query });
      return jsonOrThrow<HistoryPage>(res);
    },
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    staleTime: 0,
    retry: retryOnce,
  });
}

/**
 * The key a change carries (`Idempotency-Key`), made once for the change and sent again with each try of it, so one
 * whose answer was lost and is tried again is made once. A change confirmed, or refused as it stands, takes a new key
 * next time; one that got no answer, or a server's failure, keeps it.
 */
export function useCommandKey() {
  const key = useRef<string | null>(null);
  return {
    take: (): string => (key.current ??= crypto.randomUUID()),
    settle: (error?: unknown): void => {
      if (error === undefined || (error instanceof ApiError && error.status < 500)) key.current = null;
    },
  };
}

const ifMatch = (version: number) => `"v${version}"`;

/** A new customer, as the API takes one. */
export type CompanyBody = {
  display_name: string;
  legal_name?: string | null;
  domain?: string | null;
  website?: string | null;
  address?: CompanyAddress | null;
  notes?: string | null;
};

/** A customer's change: what it names changes, null clears it. */
export type CompanyChangeBody = Partial<CompanyBody>;

export function useCreateCompany() {
  const qc = useQueryClient();
  const command = useCommandKey();
  return useMutation({
    mutationFn: async (body: CompanyBody) => {
      const res = await api.api.v1.companies.$post({ json: body }, { headers: { "Idempotency-Key": command.take() } });
      return (await jsonOrThrow<{ company: Company }>(res)).company;
    },
    onSettled: (_data, error) => command.settle(error ?? undefined),
    onSuccess: (company) => {
      qc.setQueryData(companyKey(company.id), company);
      void qc.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

export function useUpdateCompany(id: string) {
  const qc = useQueryClient();
  const command = useCommandKey();
  return useMutation({
    mutationFn: async ({ change, version }: { change: CompanyChangeBody; version: number }) => {
      const res = await api.api.v1.companies[":id"].$patch(
        { param: { id }, json: change },
        { headers: { "If-Match": ifMatch(version), "Idempotency-Key": command.take() } },
      );
      return (await jsonOrThrow<{ company: Company }>(res)).company;
    },
    onSettled: (_data, error) => command.settle(error ?? undefined),
    onSuccess: (company) => {
      qc.setQueryData(companyKey(id), company);
      void qc.invalidateQueries({ queryKey: COMPANIES_KEY });
      void qc.invalidateQueries({ queryKey: historyKey("company", id) });
    },
    onError: (error) => {
      // Changed by someone else since it was read: the page shows it as it is now.
      if (error instanceof ApiError && error.status === 412) void qc.invalidateQueries({ queryKey: companyKey(id) });
    },
  });
}

/** A contact's email address, phone number or other way to reach them, as the API takes one. */
export type MethodBody =
  | { kind: "email" | "phone"; value: string; label?: string; is_primary?: boolean }
  | { kind: "other"; value: string; label: string; is_primary?: boolean };

/** A contact's link to a customer. */
export type CompanyLinkBody = {
  company_id: string;
  relationship?: string;
  title?: string;
  department?: string;
  is_primary?: boolean;
};

export type ContactBody = {
  display_name?: string;
  given_name?: string;
  family_name?: string;
  preferred_name?: string;
  methods?: MethodBody[];
  companies?: CompanyLinkBody[];
};

export type ContactChangeBody = {
  display_name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  preferred_name?: string | null;
  methods?: MethodBody[];
  companies?: CompanyLinkBody[];
};

export function useCreateContact() {
  const qc = useQueryClient();
  const command = useCommandKey();
  return useMutation({
    mutationFn: async (body: ContactBody) => {
      const res = await api.api.v1.contacts.$post({ json: body }, { headers: { "Idempotency-Key": command.take() } });
      return (await jsonOrThrow<{ contact: Contact }>(res)).contact;
    },
    onSettled: (_data, error) => command.settle(error ?? undefined),
    onSuccess: (contact) => {
      qc.setQueryData(contactKey(contact.id), contact);
      void qc.invalidateQueries({ queryKey: CONTACTS_KEY });
      for (const link of contact.companies)
        void qc.invalidateQueries({ queryKey: companyContactsKey(link.company_id) });
    },
  });
}

export function useUpdateContact(id: string) {
  const qc = useQueryClient();
  const command = useCommandKey();
  return useMutation({
    mutationFn: async ({ change, version }: { change: ContactChangeBody; version: number }) => {
      const res = await api.api.v1.contacts[":id"].$patch(
        { param: { id }, json: change },
        { headers: { "If-Match": ifMatch(version), "Idempotency-Key": command.take() } },
      );
      return (await jsonOrThrow<{ contact: Contact }>(res)).contact;
    },
    onSettled: (_data, error) => command.settle(error ?? undefined),
    onSuccess: (contact) => {
      qc.setQueryData(contactKey(id), contact);
      void qc.invalidateQueries({ queryKey: CONTACTS_KEY });
      void qc.invalidateQueries({ queryKey: historyKey("contact", id) });
      void qc.invalidateQueries({ queryKey: ["company"] });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 412) void qc.invalidateQueries({ queryKey: contactKey(id) });
    },
  });
}
